import React from 'react';
import { Row, Col, Card, Typography, Tag } from 'antd';
import { BankOutlined, LockOutlined, RightOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const ParentOrgIndicator = ({ selectedOrg, currentOrg, isCreating, currentUserOrgId }) => {
  if (!selectedOrg) return null;

  // Build the hierarchy path from current user org down to selected org
  const buildHierarchyPath = () => {
    if (!selectedOrg.parentOrg || selectedOrg.parentOrg.length === 0) {
      // If no parent hierarchy, just show current org
      return [currentOrg];
    }

    // Filter hierarchy to only show downward from current user's org
    const fullHierarchy = selectedOrg.parentOrg || [];
    const userOrgIndex = fullHierarchy.findIndex((org) => org.id === currentUserOrgId);

    if (userOrgIndex === -1) {
      // User org not found in hierarchy, just show current org
      return [currentOrg];
    }

    // Include user's org and all children down to selected org's parent
    const visibleHierarchy = fullHierarchy.slice(userOrgIndex);

    // If we're viewing an existing org, add it to the end
    if (!isCreating && selectedOrg.id !== currentUserOrgId) {
      visibleHierarchy.push(selectedOrg);
    }

    return visibleHierarchy;
  };

  const hierarchyPath = buildHierarchyPath();
  const parentOrgName =
    isCreating && hierarchyPath.length > 0 ? hierarchyPath[hierarchyPath.length - 1]?.name : null;

  const styles = {
    card: {
      marginBottom: '16px',
      backgroundColor: isCreating ? '#f6ffed' : '#e6f7ff',
      border: isCreating ? '1px solid #b7eb8f' : '1px solid #91d5ff',
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',
    },
    orgItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    currentOrg: {
      fontWeight: 'bold',
      color: isCreating ? '#52c41a' : '#1890ff',
    },
    newOrg: {
      fontWeight: 'bold',
      color: '#52c41a',
    },
    separator: {
      color: '#8c8c8c',
      fontSize: '12px',
    },
    lockIcon: {
      color: '#faad14',
      marginLeft: '4px',
    },
    confirmationText: {
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: isCreating ? '#f6ffed' : '#e6f7ff',
      borderRadius: '4px',
      border: isCreating ? '1px solid #d9f7be' : '1px solid #bae7ff',
    },
  };

  return (
    <Card size="small" style={styles.card}>
      <Row>
        <Col span={24}>
          <div style={styles.breadcrumb}>
            <BankOutlined style={{ color: '#8c8c8c' }} />

            {/* Dynamic hierarchy path */}
            {hierarchyPath.map((org, index) => {
              const isLast = index === hierarchyPath.length - 1;
              const isParentForNewOrg = isCreating && isLast;

              return (
                <React.Fragment key={org.id || index}>
                  <div style={styles.orgItem}>
                    <Text
                      style={{
                        ...styles.currentOrg,
                        color: isCreating ? (isParentForNewOrg ? '#52c41a' : '#1890ff') : '#1890ff',
                      }}
                    >
                      {org.name}
                    </Text>
                    {isParentForNewOrg && <LockOutlined style={styles.lockIcon} />}
                  </div>

                  {/* Add separator if not the last item or if creating new org */}
                  {(!isLast || isCreating) && <RightOutlined style={styles.separator} />}
                </React.Fragment>
              );
            })}

            {/* New Organization tag for creation mode */}
            {isCreating && <Tag color="green">New Organization</Tag>}
          </div>

          {isCreating && parentOrgName && (
            <div style={styles.confirmationText}>
              <Text type="success">
                <strong>Creating organization under:</strong> {parentOrgName}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                The new organization will inherit settings from this parent organization
              </Text>
            </div>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default ParentOrgIndicator;
